/**
 * Wiring for an ACCEPTED inbound call (split out of use-inbound-voice.ts to
 * keep both under the component-size budget). Hands the ringing Call to the
 * provider's shared state machine: same per-call reset, same idempotent
 * finishCall contract, same socket safety net — with the inbound-flavored
 * teardown (stop local tracks + recycle the receiver Device; that rebuild is
 * what releases the mic, mirroring the outbound per-call-Device destroy).
 */
import type { Call, Device } from "@twilio/voice-sdk";
import * as Sentry from "@sentry/react";
import { useSocketContext } from "./socket.context/socket.context";
import type { CallEndReason } from "./twilio.context";
import type { IncomingCallInfo, InboundVoiceDeps } from "./use-inbound-voice";

const LOG = "[Twilio]";

type SocketLike = ReturnType<typeof useSocketContext>;

export function acceptInboundCall(p: {
  call: Call;
  info: IncomingCallInfo;
  deps: InboundVoiceDeps;
  socket: SocketLike;
  /** The receiver Device the call lives on (for audio processors). */
  device: Device | null;
  /** Rebuild the receiver once the call is terminal (mic release). */
  recycleReceiver: () => void;
}): void {
  const { call, info, deps: d, socket } = p;

  d.beginCall({
    // A withheld caller with no lead match would leave the floating indicator
    // (this call's only surface) with a blank identity — name it explicitly.
    leadName: info.leadName ?? (info.fromE164 ? null : "Unknown caller"),
    phoneNumber: info.fromE164,
    leadId: info.leadId,
    sessionName: info.sessionName || null,
  });
  d.callRef.current = call;
  d.activeDeviceRef.current = p.device;
  d.setState("connecting");

  let terminated = false;
  const finishCall = (
    reason: CallEndReason,
    nextState: "ended" | "error",
    source: string,
  ): boolean => {
    if (terminated) {
      console.warn(`${LOG} inbound call.${source} fired AFTER terminal (ignored)`);
      return false;
    }
    terminated = true;
    d.finishCallRef.current = null;
    socket.off("call.ended", onCallEndedFromBackend);
    d.cleanupSocketRef.current = null;
    const effectiveReason = d.networkTerminatedRef.current ? "failed" : reason;
    const effectiveNextState = d.networkTerminatedRef.current ? "error" : nextState;
    d.networkTerminatedRef.current = false;
    // Stop this call's local tracks a tick later (zombie-leg path, same
    // reasoning as the outbound finishCall's deferred teardown).
    const staleLocalStream = d.callRef.current?.getLocalStream();
    window.setTimeout(() => {
      try {
        staleLocalStream?.getTracks().forEach((track) => track.stop());
      } catch { /* noop */ }
    }, 0);
    d.activeDeviceRef.current = null;
    p.recycleReceiver();
    d.setCallEndReason(effectiveReason);
    d.callRef.current = null;
    d.callInFlightRef.current = false;
    d.setState(effectiveNextState);
    return true;
  };
  d.finishCallRef.current = finishCall;

  // Backend-driven terminal event, matched by sessionName — kept for inbound
  // as a safety net even though the agent leg is a Client leg whose own
  // `disconnect` is reliable (see the outbound path's rationale).
  const onCallEndedFromBackend = (payload?: { sessionName?: string; reason?: string }) => {
    if (!info.sessionName) return;
    if (payload?.sessionName && payload.sessionName !== info.sessionName) return;
    finishCall("completed", "ended", "socket:call.ended");
  };
  socket.on("call.ended", onCallEndedFromBackend);
  d.cleanupSocketRef.current = () => socket.off("call.ended", onCallEndedFromBackend);

  call.on("accept", () => {
    d.setCallStartedAt(new Date());
    d.setState("in-call");
  });
  call.on("reconnecting", () => d.setIsReconnecting(true));
  call.on("reconnected", () => d.setIsReconnecting(false));
  call.on("disconnect", () => finishCall("completed", "ended", "disconnect"));
  call.on("cancel", () => finishCall("no-answer", "ended", "cancel"));
  call.on("error", (err: { message?: string; code?: number }) => {
    Sentry.captureException(
      err instanceof Error ? err : new Error(`${LOG} inbound ${err?.message ?? "Unknown call error"}`),
      {
        tags: { component: "InboundVoice", source: "Call.on(error)" },
        extra: { sessionName: info.sessionName, twilioCode: err?.code },
      },
    );
    d.setErrorMessage(err?.message ?? "Call error");
    finishCall("failed", "error", "error");
  });
  call.on("mute", (muted: boolean) => {
    d.setIsMuted(muted);
    d.currentMutedRef.current = muted;
  });

  // Mic permission is requested here; a denial surfaces via `error` above. A
  // synchronous throw (destroyed device edge) must still release the call slot
  // — otherwise callInFlightRef stays true and every Call button bricks.
  try {
    call.accept();
  } catch (err) {
    Sentry.captureException(err, {
      tags: { component: "InboundVoice", operation: "accept" },
      extra: { sessionName: info.sessionName },
    });
    d.setErrorMessage("Could not answer the call");
    finishCall("failed", "error", "accept-throw");
  }
}
